import { computed } from 'vue'
import { FindexClient } from '@/integrations/findex'
import { companyProfileToOption } from '@/form/options/companyProfile'
import type { UseAssetFormState } from '../types'
import { defineField, Validators, type FormFieldDefinition } from '@/form/useForm'
import type { ICompanyProfile, ISavedDocument } from 'findef'
import { debounce } from '@/utils/async/debounce'
const debouncedQueryTicCompanies = debounce((query: string) => FindexClient.queryTicCompanies(100, query), 300)

export const unlistedCompanyProfileField = (listed: boolean, needs: string[] = ['isEditMode', 'asset.listed', 'asset.type']): FormFieldDefinition<UseAssetFormState, ICompanyProfile | ISavedDocument<ICompanyProfile, string>> => {
  return defineField({
    label: listed ? 'Equity Name' : 'Company Name',
    component: 'select2',
    type: 'dbObject',
    validators: [Validators.required()],
    required: true,
    placeholder: listed ? 'Search tickers...' : 'Search companies...',
    injectedProps: {
      icon: {
        src: async () => import('assets/img/svg/icons/search.svg?raw'),
        stroke: 'currentColor',
        size: '1rem'
      }
    },
    needs: needs,
    options: {
      toOption: (val) => companyProfileToOption(val, listed),
      name: 'ticker-search-dropdown',
      warning: undefined,
      add: {
        invokeOnPressEnter: true,
        createItem: async (name) => {
          const [company] = await FindexClient.createCompanyProfile({
            companyName: name,
            listed: listed,
            manuallyAdded: true
          })

          if (!company) {
            return {
              label: 'Error',
              value: null
            }
          }

          return companyProfileToOption(company, listed)
        }
      },
      search: {
        placeholder: listed ? 'Search tickers...' : 'Search companies...',
        emptyStateText: listed ? 'No listed tickers found.' : 'Start typing to search among 1.9 million Swedish companies',
        api: {
          fetch: async (query: string) => {
            const [items] = await debouncedQueryTicCompanies(query) as Awaited<ReturnType<typeof FindexClient.queryTicCompanies>>;
            return (items || []).map((it) => companyProfileToOption(it, listed))
          },
          fetchAll: async () => {
            const [items] = await FindexClient.queryTicCompanies(100);
            return (items || []).map((it) => companyProfileToOption(it, listed))
          },
          fetchSuggestions: async () => {
            const [items] = await FindexClient.queryTicCompanies(100)
            return (items || []).map((it) => companyProfileToOption(it, listed))
          }
        }
      },
      maxVisibleItems: 4
    }
  })
}
